import { Container, SCALE_MODES, Texture, TilingSprite, WRAP_MODES } from "pixi.js"
import { PixelBuffer, hexOf, mix, rgb } from "./pixel_raster"
import { PixelLayer } from "./pixel_layer"
import {
    BACKDROP_DONE,
    Backdrop,
    PX,
    artRect,
    buildBackdrop,
    composeBackdrop,
    composePines,
    pineFrameAt,
    pinesRect,
    shoreRowAt,
} from "./pixel_shipwreck_art"
import {
    Flash,
    HORIZON_ROW,
    MOON_ART,
    RainSheet,
    ScudStrip,
    SwellRow,
    buildRain,
    buildScud,
    buildSwell,
    flashAt,
    flashSky,
    flashSolid,
    paintBolt,
    paintSurf,
    paintSwell,
    rainAt,
    rainOffset,
    scudOffset,
} from "./pixel_storm_art"
import { Bounds, MIRROR_AXIS, SCENE, SHIP_ON_RIGHT, prog } from "./shipwreck_geometry"

/** Inside this many art pixels of the moon's centre the flash leaves the disc alone. */
const MOON_CLEAR = 11

/** The rain's colour in a strike: its streaks near white. */
const RAIN_HOT = 0xc8e8e8

/**
 * The storm night on screen: the login's shipwreck and character creation (over the ship's rail)
 * both stand in it. Two containers, so whatever stands in the storm goes between them: `back` (sky,
 * scud, lightning, sea, the far shore) and `front` (the rain).
 *
 * Every layer is pixel art from pixel_storm_art and pixel_shipwreck_art, and each is repainted only
 * when something it shows changes: the sky and the land while they dissolve in and when a strike
 * changes its stage, the scud when it has drifted a whole pixel, the sea and surf at half the frame
 * rate. The rain is two repeating sprites, moved a whole art pixel at a time, never repainted.
 */
export class StormNight {
    readonly back = new Container()
    readonly front = new Container()

    private readonly sky = new PixelLayer()
    private readonly scudLayers: PixelLayer[]
    private readonly sea = new PixelLayer()
    private readonly land = new PixelLayer()
    private readonly pines = new PixelLayer()
    private readonly surf = new PixelLayer()
    private readonly scud: ScudStrip[] = buildScud()
    private readonly rain: RainSheet[] = buildRain()
    private readonly rainSprites: TilingSprite[]

    private backdrop?: Backdrop
    private swell: SwellRow[] = []
    private shore: (x: number) => number = () => 0
    private bufs?: { sky: PixelBuffer; scud: PixelBuffer[]; sea: PixelBuffer; land: PixelBuffer; pines: PixelBuffer; surf: PixelBuffer }
    private beach = { x0: 0, x1: 0 }
    private keys = { sky: "", scud: [] as string[], land: "", pines: "", sea: "" }
    private frame = 0

    /** `lightning` false for a player who asked for reduced motion: rain and sea, but no flashes. */
    constructor(private readonly lightning: boolean = true) {
        this.scudLayers = this.scud.map(() => new PixelLayer())
        this.rainSprites = this.rain.map(sheet => {
            const rgba = new Uint8Array(sheet.tile.w * sheet.tile.h * 4)
            // Mirrored like every other layer, so the streaks slant toward the land on screen too.
            sheet.tile.writeRgba(new Uint32Array(rgba.buffer), SHIP_ON_RIGHT)
            const texture = Texture.fromBuffer(rgba, sheet.tile.w, sheet.tile.h, { scaleMode: SCALE_MODES.NEAREST, wrapMode: WRAP_MODES.REPEAT })
            const sprite = new TilingSprite(texture, 1, 1)
            sprite.tileScale.set(PX)
            sprite.tint = sheet.tint
            return sprite
        })
        this.back.addChild(this.sky.sprite, ...this.scudLayers.map(l => l.sprite), this.sea.sprite, this.land.sprite, this.pines.sprite, this.surf.sprite)
        this.front.addChild(...this.rainSprites)
    }

    /** Lay the storm out for a rectangle of the authored scene (logical pixels). */
    build = (b: Bounds) => {
        const r = artRect(b)
        const backdrop = buildBackdrop(b)
        this.backdrop = backdrop
        this.swell = buildSwell(b)
        this.shore = shoreRowAt(b.right)

        const sky = new PixelBuffer(r.w, Math.max(1, HORIZON_ROW - r.y0), r.x0, r.y0)
        const scud = this.scud.map(strip => new PixelBuffer(r.w, strip.tile.h, r.x0, strip.tile.y0))
        const sea = new PixelBuffer(r.w, Math.max(1, r.y0 + r.h - HORIZON_ROW), r.x0, HORIZON_ROW)
        const landRect = groupRect(backdrop)
        const land = new PixelBuffer(landRect.w, landRect.h, landRect.x0, landRect.y0)
        const pr = pinesRect(backdrop)
        const pines = new PixelBuffer(pr.w, pr.h, pr.x0, pr.y0)
        // The beach the surf runs up: from the foot of the hill to the edge of the view.
        this.beach = { x0: Math.max(r.x0, Math.floor(614 / PX) - 4), x1: r.x0 + r.w }
        let top = Infinity, bottom = -Infinity
        for (let x = this.beach.x0; x < this.beach.x1; x++) {
            top = Math.min(top, this.shore(x))
            bottom = Math.max(bottom, this.shore(x))
        }
        const surf = new PixelBuffer(Math.max(1, this.beach.x1 - this.beach.x0), Math.max(1, Math.ceil(bottom) - Math.floor(top) + 9), this.beach.x0, Math.floor(top) - 5)
        this.bufs = { sky, scud, sea, land, pines, surf }

        for (const [layer, buf] of [[this.sky, sky], [this.sea, sea], [this.land, land], [this.pines, pines], [this.surf, surf]] as [PixelLayer, PixelBuffer][]) {
            layer.place(buf.x0, buf.y0, buf.w, buf.h)
        }
        this.scudLayers.forEach((layer, i) => layer.place(scud[i].x0, scud[i].y0, scud[i].w, scud[i].h))

        // The rain covers the view, on the art grid; the mirror maps the authored rectangle onto the screen.
        const left = SHIP_ON_RIGHT ? 2 * MIRROR_AXIS - (r.x0 + r.w) * PX : r.x0 * PX
        this.rainSprites.forEach(sprite => {
            sprite.position.set(left, r.y0 * PX)
            sprite.width = r.w * PX
            sprite.height = r.h * PX
        })
        this.keys = { sky: "", scud: [], land: "", pines: "", sea: "" }
    }

    /** The storm at choreography time `t`. */
    paint = (t: number) => {
        const backdrop = this.backdrop
        const bufs = this.bufs
        if (!backdrop || !bufs) return
        this.frame++
        const flash = this.lightning ? flashAt(t) : undefined
        const fkey = flash?.key ?? ""
        // While the backdrop dissolves in its layers are recomposed at half the frame rate; then only for a strike.
        const reveal = t <= BACKDROP_DONE + 100 ? `r${this.frame >> 1}` : "done"
        const level = (x: number, y: number) => (flash ? flash.level(x, y) : 0)

        if (this.keys.sky !== `${reveal}|${fkey}`) {
            this.keys.sky = `${reveal}|${fkey}`
            const out = bufs.sky
            composeInto(out, backdrop, t, "sky")
            if (flash) {
                for (let j = 0; j < out.h; j++) {
                    const y = out.y0 + j
                    for (let i = 0; i < out.w; i++) {
                        const x = out.x0 + i
                        if (Math.abs(x - MOON_ART[0]) < MOON_CLEAR && Math.hypot(x - MOON_ART[0], y - MOON_ART[1]) < MOON_CLEAR) continue
                        const lv = flash.level(x, y)
                        if (lv) out.data[j * out.w + i] = flashSky(out.data[j * out.w + i], lv)
                    }
                }
                for (const s of flash.strikes) paintBolt(out, s.bolt, s.brightness)
            }
            this.sky.commit(out)
        }

        this.scud.forEach((strip, n) => {
            const off = scudOffset(strip, t)
            const key = `${off}|${fkey}`
            if (this.keys.scud[n] === key) return
            this.keys.scud[n] = key
            const out = bufs.scud[n]
            const tile = strip.tile
            out.clear()
            for (let j = 0; j < tile.h; j++) {
                const y = tile.y0 + j
                for (let i = 0; i < out.w; i++) {
                    const x = out.x0 + i
                    const c = tile.data[j * tile.w + ((((x - off) % tile.w) + tile.w) % tile.w)]
                    if (c) out.data[j * out.w + i] = flash ? flashSky(c, level(x, y)) : c
                }
            }
            this.scudLayers[n].commit(out)
        })

        // The sea and the surf at half the frame rate: a wave moves a pixel every few frames at most.
        const seaKey = `${this.frame >> 1}|${fkey}`
        if (this.keys.sea !== seaKey) {
            this.keys.sea = seaKey
            // Over the backdrop's own dark sea, so a pixel the swell misses (or hasn't dissolved in yet) is never a hole.
            copyRows(bufs.sea, backdrop.base)
            paintSwell(bufs.sea, this.swell, t, 0, Infinity, prog(t, SCENE.water[0], SCENE.water[1]), flash)
            this.sea.commit(bufs.sea)
            bufs.surf.clear()
            if (t > SCENE.land[0] + 800) {
                paintSurf(bufs.surf, t, this.beach.x0, this.beach.x1, this.shore)
            }
            this.surf.commit(bufs.surf)
        }

        if (this.keys.land !== `${reveal}|${fkey}`) {
            this.keys.land = `${reveal}|${fkey}`
            bufs.land.clear()
            composeBackdrop(bufs.land, backdrop, t, "land", false)
            if (flash) silhouette(bufs.land, flash)
            this.land.commit(bufs.land)
        }

        const pinesKey = `${reveal}|${t >= SCENE.land[1] ? pineFrameAt(t) : ""}|${fkey}`
        if (this.keys.pines !== pinesKey) {
            this.keys.pines = pinesKey
            composePines(bufs.pines, backdrop, t)
            if (flash) silhouette(bufs.pines, flash)
            this.pines.commit(bufs.pines)
        }

        const strength = rainAt(t)
        const hot = !!flash && flash.hot
        this.rain.forEach((sheet, i) => {
            const sprite = this.rainSprites[i]
            sprite.visible = strength > 0
            const [ox, oy] = rainOffset(sheet, t)
            sprite.tilePosition.set((SHIP_ON_RIGHT ? -ox : ox) * PX, oy * PX)
            sprite.alpha = Math.min(1, sheet.alpha * strength * (hot ? 1.3 : 1))
            sprite.tint = hot ? hexOf(mix(rgb(sheet.tint), rgb(RAIN_HOT), 0.6)) : sheet.tint
        })
    }

    dispose = () => {
        ;[this.sky, ...this.scudLayers, this.sea, this.land, this.pines, this.surf].forEach(layer => layer.dispose())
        this.rainSprites.forEach(sprite => sprite.destroy({ texture: true, baseTexture: true }))
        this.back.destroy({ children: true })
        this.front.destroy({ children: true })
        this.backdrop = undefined
        this.bufs = undefined
    }
}

/** Copy the rows of `src` that `out` covers (same columns). */
const copyRows = (out: PixelBuffer, src: PixelBuffer) => {
    for (let j = 0; j < out.h; j++) {
        const from = (out.y0 + j - src.y0) * src.w + (out.x0 - src.x0)
        out.data.set(src.data.subarray(from, from + out.w), j * out.w)
    }
}

/** The sky's half of the backdrop into a buffer that covers only the sky. */
const composeInto = (out: PixelBuffer, backdrop: Backdrop, t: number, group: "sky") => {
    // composeBackdrop copies the whole base; the sky buffer is the base's top rows.
    copyRows(out, backdrop.base)
    composeBackdrop(out, { ...backdrop, base: out }, t, group)
}

/** The rectangle the land's pieces cover. */
const groupRect = (backdrop: Backdrop) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const l of backdrop.layers) {
        if (l.group !== "land" || l.sway) continue
        x0 = Math.min(x0, l.buf.x0); y0 = Math.min(y0, l.buf.y0); x1 = Math.max(x1, l.buf.x0 + l.buf.w); y1 = Math.max(y1, l.buf.y0 + l.buf.h)
    }
    return x1 < x0 ? { x0: 0, y0: 0, w: 1, h: 1 } : { x0, y0, w: x1 - x0, h: y1 - y0 }
}

/** Solid things in front of a strike go toward silhouette. */
const silhouette = (buf: PixelBuffer, flash: Flash) => {
    for (let i = 0; i < buf.data.length; i++) {
        const c = buf.data[i]
        if (c) buf.data[i] = flashSolid(c, flash.level(buf.x0 + (i % buf.w), buf.y0 + Math.floor(i / buf.w)))
    }
}
